// Pure helpers for the fan pages' default look (FanAccount.jsx, the fan's own
// /fan-account, and FanProfile.jsx, the public /fan/:uid). Unit-tested in
// fanModel.test.js.
import { cosignRows } from '../home/sceneModel';

const DAY = 86400000;

// bookmarkedAt in any shape it arrives in (Firestore Timestamp, Date, the
// ISO string the backend's jsonify() makes, {seconds}) -> epoch ms, or 0.
export function cosignMillis(at) {
  if (!at) return 0;
  if (typeof at.toDate === 'function') return at.toDate().getTime();
  if (typeof at.seconds === 'number') return at.seconds * 1000;
  if (typeof at.getTime === 'function') return at.getTime();
  const ms = new Date(at).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

// artistKey -> { motion, sincePct, fromMotion } for the co-signs that are on
// the board (the same numbers as the homepage's "Your co-signs this week").
export function cosignMotion(bookmarks, board) {
  const normalized = (bookmarks || []).map((b) => {
    const ms = cosignMillis(b?.bookmarkedAt);
    return ms ? { ...b, bookmarkedAt: { seconds: ms / 1000 } } : b;
  });
  const out = {};
  for (const row of cosignRows(normalized, board).rows) {
    out[row.artistKey] = { motion: row.motion, sincePct: row.sincePct, fromMotion: row.fromMotion };
  }
  // The board's avatar (owner photo or cached Soundcharts image, see the
  // backend's artist_overlay_service), for every co-sign the board knows.
  for (const b of normalized) {
    const photoURL = board?.artistsCollection?.[b?.artistKey]?.photoURL;
    if (photoURL) out[b.artistKey] = { ...out[b.artistKey], photoURL };
  }
  return out;
}

// "Up 202% since" / "Down 12% since" / "Flat since"; null with nothing to say.
export function sinceLine(pct) {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return null;
  const r = Math.round(pct);
  if (r === 0) return { text: 'Flat since', tone: 'flat' };
  return r > 0 ? { text: `Up ${r.toLocaleString()}% since`, tone: 'up' } : { text: `Down ${Math.abs(r).toLocaleString()}% since`, tone: 'down' };
}

// "Detroit, MI · Co-signed Mar 2 at 18.2" (the year only when it isn't this
// year's).
export function cosignMeta(artist, fromMotion, now = Date.now()) {
  const where = [artist?.homeTown, artist?.homeState].filter(Boolean).join(', ');
  const ms = cosignMillis(artist?.bookmarkedAt);
  let when = '';
  if (ms) {
    when = `Co-signed ${shortDate(ms, now)}`;
    if (Number.isFinite(fromMotion)) when += ` at ${fromMotion.toFixed(1)}`;
  }
  return [where, when].filter(Boolean).join(' · ') || 'Location unknown';
}

// "Mar 2", with the year only when it isn't this year's.
function shortDate(ms, now) {
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString('en-US', sameYear ? { month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric', year: 'numeric' });
}

// "2h", "5d", "3w"; "now" under a minute.
export function timeAgo(at, now = Date.now()) {
  const ms = cosignMillis(at);
  if (!ms) return '';
  const diff = Math.max(0, now - ms);
  if (diff < 60000) return 'now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
  if (diff < DAY) return `${Math.floor(diff / 3600000)}h`;
  if (diff < 7 * DAY) return `${Math.floor(diff / DAY)}d`;
  if (diff < 365 * DAY) return `${Math.floor(diff / (7 * DAY))}w`;
  return `${Math.floor(diff / (365 * DAY))}y`;
}

// Badge pill colour by tier: founding yellow, career indigo, the rest green.
export function badgeTone(tier) {
  if (tier === 'founding') return 'yellow';
  if (tier === 'career') return 'indigo';
  return 'green';
}

export function initialsOf(name, fallback = '?') {
  return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || fallback;
}

// The one co-signs list ("Co-signed Artists", issue #260): the fan's ranked
// co-signs first, in their ranking order, then everything else they've
// co-signed in the order the users doc holds them.
// -> [{ artist, rank }], rank being 1..n for the ranked ones and null after.
//
// Ranks are renumbered 1..n here rather than read off the ranking doc's
// `position`: a position goes stale as soon as an artist is unranked or
// un-co-signed (see rankingUtils.archiveRanking), which would otherwise show
// as a hole in the list. A ranking for an artist no longer co-signed is
// dropped - this list only ever shows co-signs.
export function cosignVaultRows(bookmarks, rankings) {
  const byKey = new Map();
  (bookmarks || []).forEach((a) => {
    if (a && a.artistKey && !byKey.has(a.artistKey)) byKey.set(a.artistKey, a);
  });
  const ranked = [];
  const used = new Set();
  (rankings || []).forEach((r) => {
    const key = r && r.artistKey;
    if (!key || used.has(key) || !byKey.has(key)) return;
    used.add(key);
    ranked.push({ artist: byKey.get(key), rank: ranked.length + 1 });
  });
  const rest = (bookmarks || [])
    .filter((a) => a && !(a.artistKey && used.has(a.artistKey)))
    .map((a) => ({ artist: a, rank: null }));
  return [...ranked, ...rest];
}

// The line under the "Co-signed Artists" heading, which says what the fan is
// holding: "Backing since Oct 18 · 2 up since you co-signed · 1 down". Only
// movement that happened is mentioned - a fan whose co-signs are all flat
// sees just the "backing since" part, never "0 up". '' with nothing co-signed.
// `motion` is cosignMotion's artistKey -> { sincePct }.
export function vaultSummaryLine(bookmarks, motion, now = Date.now()) {
  const list = (bookmarks || []).filter(Boolean);
  if (!list.length) return '';
  const times = list.map((b) => cosignMillis(b.bookmarkedAt)).filter(Boolean);
  let up = 0;
  let down = 0;
  for (const b of list) {
    const pct = motion?.[b.artistKey]?.sincePct;
    if (!Number.isFinite(pct)) continue;
    const r = Math.round(pct);
    if (r > 0) up += 1;
    else if (r < 0) down += 1;
  }
  const parts = [
    times.length
      ? `Backing since ${shortDate(Math.min(...times), now)}`
      : `Backing ${list.length} ${list.length === 1 ? 'artist' : 'artists'}`,
  ];
  if (up) parts.push(`${up} up since you co-signed`);
  if (down) parts.push(`${down} down`);
  return parts.join(' · ');
}
